import React, { useState, useEffect } from 'react';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  AreaChart,
  Area
} from 'recharts';
import { TrendingUp, TrendingDown, DollarSign, ShoppingCart, Target } from 'lucide-react';
import { saleService } from '../../services/saleService';
import { purchaseService } from '../../services/purchaseService';
import { orderService } from '../../services/orderService';
import { SaleWithDetails } from '../../types/sale';
import { PurchaseWithProduct } from '../../types/purchase';
import { OrderWithDetails } from '../../types/order';

interface DashboardData {
  monthlyPurchases: any[];
  monthlySales: any[];
  monthlyProfits: any[];
  salesByStatus: any[];
  topProducts: any[];
  profitMarginTrend: any[];
  salesVsPurchases: any[];
  kpis: {
    totalRevenue: number;
    totalPurchases: number;
    totalProfit: number;
    profitMargin: number;
    growthRate: number;
    averageOrderValue: number;
  };
}

const DashboardCharts = () => {
  const [data, setData] = useState<DashboardData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadDashboardData = async () => {
      try {
        setIsLoading(true);
        
        // Obtener datos de ventas, compras y órdenes
        const [sales, purchases, orders] = await Promise.all([
          saleService.getSales(),
          purchaseService.getPurchases(),
          orderService.getOrders()
        ]);

        // Procesar datos para gráficos
        const processedData = processDashboardData(sales, purchases, orders);
        setData(processedData);
      } catch (err) {
        console.error('Error cargando datos del dashboard:', err);
        setError('Error al cargar los datos del dashboard');
      } finally {
        setIsLoading(false);
      }
    };

    loadDashboardData();
  }, []);

  const processDashboardData = (
    sales: SaleWithDetails[],
    purchases: PurchaseWithProduct[],
    orders: OrderWithDetails[]
  ): DashboardData => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth();

    // Generar últimos 12 meses
    const months = [];
    for (let i = 11; i >= 0; i--) {
      const date = new Date(currentYear, currentMonth - i, 1);
      months.push({
        key: `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`,
        label: date.toLocaleDateString('es-ES', { month: 'short', year: '2-digit' }),
        year: date.getFullYear(),
        month: date.getMonth()
      });
    }

    // Procesar datos mensuales de compras
    const monthlyPurchases = months.map(month => {
      const monthPurchases = purchases.filter(p => {
        const purchaseDate = new Date(p.purchase_date);
        return purchaseDate.getFullYear() === month.year && 
               purchaseDate.getMonth() === month.month;
      });

      const totalAmount = monthPurchases.reduce((sum, p) => sum + p.total_cost, 0);
      
      return {
        month: month.label,
        gastos: Math.round(totalAmount * 100) / 100
      };
    });

    // Procesar datos mensuales de ventas
    const monthlySales = months.map(month => {
      const monthSales = sales.filter(s => {
        const saleDate = new Date(s.sale_date);
        return saleDate.getFullYear() === month.year && 
               saleDate.getMonth() === month.month;
      });

      const totalRevenue = monthSales.reduce((sum, s) => sum + s.subtotal, 0);
      const totalProfit = monthSales.reduce((sum, s) => sum + s.net_profit, 0);
      
      return {
        month: month.label,
        ventas: Math.round(totalRevenue * 100) / 100,
        ganancia: Math.round(totalProfit * 100) / 100
      };
    });

    // Datos para comparativo ventas vs compras
    const salesVsPurchases = months.map((month, index) => ({
      month: month.label,
      Ventas: monthlySales[index]?.ventas || 0,
      Compras: monthlyPurchases[index]?.gastos || 0
    }));

    // Estado de ventas
    const salesByStatus = [
      { name: 'Generadas', value: sales.filter(s => s.status === 'Generada').length, color: '#3B82F6' },
      { name: 'Facturadas', value: sales.filter(s => s.status === 'Facturada').length, color: '#10B981' },
      { name: 'Completadas', value: sales.filter(s => s.status === 'Completada').length, color: '#059669' },
      { name: 'Vencidas', value: sales.filter(s => s.status === 'Vencida').length, color: '#EF4444' }
    ];

    // Top productos más vendidos
    const productSales = sales.reduce((acc, sale) => {
      const productName = sale.product?.name || 'Producto desconocido';
      if (!acc[productName]) {
        acc[productName] = { quantity: 0, revenue: 0 };
      }
      acc[productName].quantity += sale.quantity;
      acc[productName].revenue += sale.subtotal;
      return acc;
    }, {} as Record<string, { quantity: number; revenue: number }>);

    const topProducts = Object.entries(productSales)
      .map(([name, data]) => ({
        name: name.length > 15 ? name.substring(0, 15) + '...' : name,
        quantity: data.quantity,
        revenue: Math.round(data.revenue * 100) / 100
      }))
      .sort((a, b) => b.revenue - a.revenue)
      .slice(0, 5);

    // Tendencia de margen de ganancia
    const profitMarginTrend = monthlySales.map(month => ({
      month: month.month,
      margen: month.ventas > 0 ? Math.round(((month.ganancia / month.ventas) * 100) * 100) / 100 : 0
    }));

    // KPIs
    const totalRevenue = sales.reduce((sum, s) => sum + s.subtotal, 0);
    const totalPurchases = purchases.reduce((sum, p) => sum + p.total_cost, 0);
    const totalProfit = sales.reduce((sum, s) => sum + s.net_profit, 0);
    const profitMargin = totalRevenue > 0 ? (totalProfit / totalRevenue) * 100 : 0;
    
    // Calcular crecimiento (últimos 3 meses vs 3 meses anteriores)
    const last3Months = monthlySales.slice(-3);
    const prev3Months = monthlySales.slice(-6, -3);
    const currentPeriodRevenue = last3Months.reduce((sum, m) => sum + m.ventas, 0);
    const previousPeriodRevenue = prev3Months.reduce((sum, m) => sum + m.ventas, 0);
    const growthRate = previousPeriodRevenue > 0 
      ? ((currentPeriodRevenue - previousPeriodRevenue) / previousPeriodRevenue) * 100 
      : 0;

    const averageOrderValue = sales.length > 0 ? totalRevenue / sales.length : 0;

    return {
      monthlyPurchases,
      monthlySales,
      monthlyProfits: profitMarginTrend,
      salesByStatus,
      topProducts,
      profitMarginTrend,
      salesVsPurchases,
      kpis: {
        totalRevenue: Math.round(totalRevenue * 100) / 100,
        totalPurchases: Math.round(totalPurchases * 100) / 100,
        totalProfit: Math.round(totalProfit * 100) / 100,
        profitMargin: Math.round(profitMargin * 100) / 100,
        growthRate: Math.round(growthRate * 100) / 100,
        averageOrderValue: Math.round(averageOrderValue * 100) / 100
      }
    };
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="bg-white p-6 rounded-lg shadow-sm border">
              <div className="animate-pulse">
                <div className="h-4 bg-gray-200 rounded mb-4"></div>
                <div className="h-8 bg-gray-200 rounded mb-2"></div>
                <div className="h-3 bg-gray-200 rounded w-16"></div>
              </div>
            </div>
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="bg-white p-6 rounded-lg shadow-sm border">
              <div className="animate-pulse">
                <div className="h-6 bg-gray-200 rounded mb-4"></div>
                <div className="h-64 bg-gray-200 rounded"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="bg-white p-6 rounded-lg shadow-sm border">
        <div className="text-center text-red-600">
          <p>{error || 'Error al cargar los datos'}</p>
        </div>
      </div>
    );
  }

  const CurrencyTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border rounded-lg shadow-lg">
          <p className="font-medium">{label}</p>
          {payload.map((entry: any, index: number) => (
            <p key={index} style={{ color: entry.color }}>
              {`${entry.dataKey}: S/ ${entry.value.toLocaleString()}`}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* KPIs Cards */}
      <div className="bg-white p-4 sm:p-6 lg:p-8 rounded-lg shadow-sm border">
        <h3 className="text-lg sm:text-xl font-bold mb-4 sm:mb-6 text-gray-900">Métricas Clave del Negocio</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
          {/* Ingresos Totales */}
          <div className="bg-gradient-to-br from-emerald-50 via-green-50 to-emerald-100 p-4 sm:p-5 lg:p-7 rounded-xl border border-emerald-200 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <div className="flex items-center mb-2">
                  <div className="p-1.5 sm:p-2 bg-emerald-200 rounded-lg mr-2 sm:mr-3">
                    <DollarSign className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-700" />
                  </div>
                  <p className="text-xs sm:text-sm font-semibold text-emerald-700">Ingresos Totales</p>
                </div>
                <p className="text-2xl sm:text-3xl lg:text-4xl font-bold text-emerald-800 mb-2 sm:mb-3">
                  S/ {data.kpis.totalRevenue.toLocaleString()}
                </p>
                <div className="flex items-center">
                  {data.kpis.growthRate >= 0 ? (
                    <TrendingUp className="w-3 h-3 sm:w-4 sm:h-4 text-emerald-600 mr-1 sm:mr-2" />
                  ) : (
                    <TrendingDown className="w-3 h-3 sm:w-4 sm:h-4 text-red-500 mr-1 sm:mr-2" />
                  )}
                  <span className={`text-xs sm:text-sm font-bold ${data.kpis.growthRate >= 0 ? 'text-emerald-700' : 'text-red-600'}`}>
                    {Math.abs(data.kpis.growthRate)}%
                  </span>
                  <span className="text-xs text-emerald-600 ml-1 hidden sm:inline">vs mes anterior</span>
                </div>
              </div>
            </div>
          </div>

          {/* Gastos Totales */}
          <div className="bg-gradient-to-br from-red-50 via-rose-50 to-red-100 p-4 sm:p-5 lg:p-7 rounded-xl border border-red-200 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <div className="flex items-center mb-2">
                  <div className="p-1.5 sm:p-2 bg-red-200 rounded-lg mr-2 sm:mr-3">
                    <ShoppingCart className="w-4 h-4 sm:w-5 sm:h-5 text-red-700" />
                  </div>
                  <p className="text-xs sm:text-sm font-semibold text-red-700">Gastos Totales</p>
                </div>
                <p className="text-2xl sm:text-3xl lg:text-4xl font-bold text-red-800 mb-2 sm:mb-3">
                  S/ {data.kpis.totalPurchases.toLocaleString()}
                </p>
                <p className="text-xs sm:text-sm text-red-600">En compras realizadas</p>
              </div>
            </div>
          </div>

          {/* Ganancia Neta */}
          <div className="bg-gradient-to-br from-blue-50 via-indigo-50 to-blue-100 p-4 sm:p-5 lg:p-7 rounded-xl border border-blue-200 shadow-sm hover:shadow-md transition-shadow sm:col-span-2 lg:col-span-1">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <div className="flex items-center mb-2">
                  <div className="p-1.5 sm:p-2 bg-blue-200 rounded-lg mr-2 sm:mr-3">
                    <Target className="w-4 h-4 sm:w-5 sm:h-5 text-blue-700" />
                  </div>
                  <p className="text-xs sm:text-sm font-semibold text-blue-700">Ganancia Neta</p>
                </div>
                <p className="text-2xl sm:text-3xl lg:text-4xl font-bold text-blue-800 mb-2 sm:mb-3">
                  S/ {data.kpis.totalProfit.toLocaleString()}
                </p>
                <p className="text-xs sm:text-sm text-blue-600">
                  Margen: <span className="font-bold">{data.kpis.profitMargin}%</span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Charts Grid - Responsive Layout */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 sm:gap-6">
        {/* Gastos por Compras */}
        <div className="bg-white p-4 sm:p-6 rounded-lg shadow-sm border">
          <h3 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4">Gastos por Compras (Últimos 12 meses)</h3>
          <div className="h-64 sm:h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.monthlyPurchases}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="month"
                  tick={{ fontSize: window.innerWidth < 640 ? 10 : 12 }}
                  angle={window.innerWidth < 640 ? -45 : 0}
                  textAnchor={window.innerWidth < 640 ? "end" : "middle"}
                  height={window.innerWidth < 640 ? 60 : 40}
                />
                <YAxis tickFormatter={(value) => `S/ ${value}`} tick={{ fontSize: window.innerWidth < 640 ? 10 : 12 }} />
                <Tooltip content={<CurrencyTooltip />} />
                <Bar dataKey="gastos" fill="#EF4444" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Ventas y Ganancias */}
        <div className="bg-white p-4 sm:p-6 rounded-lg shadow-sm border">
          <h3 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4">Ventas y Ganancias (Últimos 12 meses)</h3>
          <div className="h-64 sm:h-80">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data.monthlySales}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="month"
                  tick={{ fontSize: window.innerWidth < 640 ? 10 : 12 }}
                  angle={window.innerWidth < 640 ? -45 : 0}
                  textAnchor={window.innerWidth < 640 ? "end" : "middle"}
                  height={window.innerWidth < 640 ? 60 : 40}
                />
                <YAxis tickFormatter={(value) => `S/ ${value}`} tick={{ fontSize: window.innerWidth < 640 ? 10 : 12 }} />
                <Tooltip content={<CurrencyTooltip />} />
                <Legend wrapperStyle={{ fontSize: window.innerWidth < 640 ? '12px' : '14px' }} />
                <Area type="monotone" dataKey="ventas" stackId="1" stroke="#3B82F6" fill="#3B82F6" fillOpacity={0.6} />
                <Area type="monotone" dataKey="ganancia" stackId="2" stroke="#10B981" fill="#10B981" fillOpacity={0.6} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Comparativo Ventas vs Compras */}
        <div className="bg-white p-4 sm:p-6 rounded-lg shadow-sm border">
          <h3 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4">Ventas vs Compras</h3>
          <div className="h-64 sm:h-80">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data.salesVsPurchases}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="month"
                  tick={{ fontSize: window.innerWidth < 640 ? 10 : 12 }}
                  angle={window.innerWidth < 640 ? -45 : 0}
                  textAnchor={window.innerWidth < 640 ? "end" : "middle"}
                  height={window.innerWidth < 640 ? 60 : 40}
                />
                <YAxis tickFormatter={(value) => `S/ ${value}`} tick={{ fontSize: window.innerWidth < 640 ? 10 : 12 }} />
                <Tooltip content={<CurrencyTooltip />} />
                <Legend wrapperStyle={{ fontSize: window.innerWidth < 640 ? '12px' : '14px' }} />
                <Line type="monotone" dataKey="Ventas" stroke="#3B82F6" strokeWidth={2} dot={{ fill: '#3B82F6' }} />
                <Line type="monotone" dataKey="Compras" stroke="#EF4444" strokeWidth={2} dot={{ fill: '#EF4444' }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Estado de Ventas */}
        <div className="bg-white p-4 sm:p-6 rounded-lg shadow-sm border">
          <h3 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4">Estado de Ventas</h3>
          <div className="h-64 sm:h-80">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={data.salesByStatus}
                  cx="50%"
                  cy="50%"
                  innerRadius={window.innerWidth < 640 ? 40 : 60}
                  outerRadius={window.innerWidth < 640 ? 70 : 100}
                  paddingAngle={5}
                  dataKey="value"
                >
                  {data.salesByStatus.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend
                  wrapperStyle={{ fontSize: window.innerWidth < 640 ? '11px' : '14px' }}
                  layout={window.innerWidth < 640 ? "horizontal" : "vertical"}
                  verticalAlign="bottom"
                  align="center"
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Top Productos - Full width on mobile */}
        <div className="bg-white p-4 sm:p-6 rounded-lg shadow-sm border xl:col-span-2">
          <h3 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4">Top 5 Productos</h3>
          <div className="h-64 sm:h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={data.topProducts}
                layout={window.innerWidth < 640 ? "vertical" : "horizontal"}
              >
                <CartesianGrid strokeDasharray="3 3" />
                {window.innerWidth < 640 ? (
                  <>
                    <XAxis dataKey="name" tick={{ fontSize: 10 }} angle={-45} textAnchor="end" height={80} />
                    <YAxis tickFormatter={(value) => `S/ ${value}`} tick={{ fontSize: 10 }} />
                  </>
                ) : (
                  <>
                    <XAxis type="number" tickFormatter={(value) => `S/ ${value}`} tick={{ fontSize: 12 }} />
                    <YAxis dataKey="name" type="category" width={100} tick={{ fontSize: 12 }} />
                  </>
                )}
                <Tooltip content={<CurrencyTooltip />} />
                <Bar
                  dataKey="revenue"
                  fill="#8B5CF6"
                  radius={window.innerWidth < 640 ? [4, 4, 0, 0] : [0, 4, 4, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Margen de Ganancia - Full width on mobile */}
        <div className="bg-white p-4 sm:p-6 rounded-lg shadow-sm border xl:col-span-2">
          <h3 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4">Margen de Ganancia (%)</h3>
          <div className="h-64 sm:h-80">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data.profitMarginTrend}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="month"
                  tick={{ fontSize: window.innerWidth < 640 ? 10 : 12 }}
                  angle={window.innerWidth < 640 ? -45 : 0}
                  textAnchor={window.innerWidth < 640 ? "end" : "middle"}
                  height={window.innerWidth < 640 ? 60 : 40}
                />
                <YAxis tickFormatter={(value) => `${value}%`} tick={{ fontSize: window.innerWidth < 640 ? 10 : 12 }} />
                <Tooltip formatter={(value) => [`${value}%`, 'Margen']} />
                <Line
                  type="monotone"
                  dataKey="margen"
                  stroke="#F59E0B"
                  strokeWidth={3}
                  dot={{ fill: '#F59E0B', r: window.innerWidth < 640 ? 3 : 4 }}
                  activeDot={{ r: window.innerWidth < 640 ? 5 : 6 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardCharts;

// Exportar para que sea considerado un módulo
export {};